/*
 * Pure invoice math — mirrors the legacy form/detail computation (inventory §3.5)
 * so the new form, the detail page and any preview agree to the cent.
 *
 * Per line:
 *   base   = quantity × unit_price
 *   ac     = is_percentage ? base × amount/100 : amount      (allowance/charge)
 *   htLine = is_charge ? base + ac : base − ac
 *   vat    = htLine × rate/100
 *   ttc    = htLine + vat
 * Invoice: Σ htLine (HT), Σ vat, TTC, plus a VAT breakdown keyed by rate.
 */
export function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100
}

export function lineTotals(line, ratePct) {
  const base = round2((Number(line.quantity) || 0) * (Number(line.unit_price) || 0))
  const amount = Number(line.allowance_charge_amount) || 0
  const ac = line.allowance_charge_is_percentage ? round2((base * amount) / 100) : amount
  const ht = round2(line.is_charge ? base + ac : base - ac)
  const vat = round2((ht * (Number(ratePct) || 0)) / 100)
  const ttc = round2(ht + vat)
  return { base, ac, ht, vat, ttc }
}

/*
 * getRatePct(line) -> number (e.g. 21). Returns aggregate totals + a sorted
 * (desc) VAT breakdown: [{ rate, base, vat }].
 */
export function invoiceTotals(lines, getRatePct) {
  let totalHt = 0
  let totalAllowances = 0
  let totalCharges = 0
  const byRate = new Map()

  for (const line of lines || []) {
    const ratePct = Number(getRatePct(line)) || 0
    const { ac, ht } = lineTotals(line, ratePct)
    totalHt = round2(totalHt + ht)
    if (ac) {
      if (line.is_charge) totalCharges = round2(totalCharges + ac)
      else totalAllowances = round2(totalAllowances + ac)
    }
    const cur = byRate.get(ratePct) || { rate: ratePct, base: 0 }
    cur.base = round2(cur.base + ht)
    byRate.set(ratePct, cur)
  }

  // VAT PER RATE on the summed base (round(base × rate)) — Peppol BR-CO-17 — not by
  // summing per-line VAT, so the preview matches what the API stores and
  // TTC == HT + VAT (BR-CO-15). Same rule as the backend.
  const vatBreakdown = [...byRate.values()]
    .map((b) => ({ rate: b.rate, base: b.base, vat: round2((b.base * b.rate) / 100) }))
    .sort((a, b) => b.rate - a.rate)
  const totalVat = round2(vatBreakdown.reduce((s, b) => s + b.vat, 0))

  return {
    totalHt,
    totalVat,
    totalTtc: round2(totalHt + totalVat),
    totalAllowances,
    totalCharges,
    vatBreakdown,
  }
}
